export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;
  const workerCount = Math.max(1, Math.min(limit, items.length));
  const workers = Array.from({ length: workerCount }, async () => {
    for (;;) {
      const index = cursor++;
      if (index >= items.length) return;
      results[index] = await fn(items[index], index);
    }
  });
  await Promise.all(workers);
  return results;
}

export function createLimiter(limit: number | (() => number)): Limiter {
  const waiting: (() => void)[] = [];
  let running = 0;
  const capacity = typeof limit === "number" ? () => limit : limit;
  const drain = () => {
    while (waiting.length > 0 && running < capacity()) waiting.shift()?.();
  };
  const enqueue = <R>(task: () => Promise<R>, first: boolean) =>
    new Promise<R>((resolve, reject) => {
      const start = () => {
        running += 1;
        Promise.resolve()
          .then(task)
          .then(resolve, reject)
          .finally(() => {
            running -= 1;
            drain();
          });
      };
      if (running < capacity()) start();
      else if (first) waiting.unshift(start);
      else waiting.push(start);
    });
  return Object.assign(<R>(task: () => Promise<R>) => enqueue(task, false), {
    first: <R>(task: () => Promise<R>) => enqueue(task, true),
    wake: drain,
  });
}

export type Limiter = LimitedRun & { first: LimitedRun; wake: () => void };

type LimitedRun = <R>(task: () => Promise<R>) => Promise<R>;
